import { isApiEnabled } from "../api/config";
import { apiRequest } from "../api/client";

export type SupportEscalationSummary = {
  sessionId: string;
  userId: string;
  userName: string;
  phone: string;
  pendingRequest: string;
  status: "open" | "done";
  escalatedAt: string | null;
  updatedAt: string;
  lastMessageAt?: string | null;
  lastUserMessage?: string;
  lastAssistantMessage?: string;
  messageCount?: number;
  managerRequested?: boolean;
  isExhibitor?: boolean;
  claimedByUserId: string | null;
  claimedByName: string | null;
  claimedAt: string | null;
  eventId?: string;
  eventTitle?: string;
  eventCity?: string | null;
};

export type SupportEscalationDetail = SupportEscalationSummary & {
  messages: Array<{
    id: number;
    role: "user" | "assistant" | "system";
    content: string;
    createdAt: string;
    meta?: Record<string, unknown>;
  }>;
};

export type SupportChatListResult = {
  items: SupportEscalationSummary[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

type ApiSession = {
  id: string;
  userId: string;
  userName: string;
  phone: string;
  pendingRequest: string;
  status: "open" | "done";
  escalatedAt: string | null;
  updatedAt: string;
  managerRequested?: boolean;
  claimedByUserId?: string | null;
  claimedByName?: string | null;
  claimedAt?: string | null;
  eventId?: string;
  messages?: SupportEscalationDetail["messages"];
};

function mapSummary(item: SupportEscalationSummary): SupportEscalationSummary {
  return {
    ...item,
    claimedByUserId: item.claimedByUserId ?? null,
    claimedByName: item.claimedByName ?? null,
    claimedAt: item.claimedAt ?? null,
    managerRequested: item.managerRequested === true,
    isExhibitor: item.isExhibitor === true,
    lastMessageAt: item.lastMessageAt ?? null,
    eventId: item.eventId,
    eventTitle: item.eventTitle,
    eventCity: item.eventCity ?? null,
  };
}

function mapSession(s: ApiSession, eventId?: string): SupportEscalationDetail {
  return {
    sessionId: s.id,
    userId: s.userId,
    userName: s.userName,
    phone: s.phone,
    pendingRequest: s.pendingRequest,
    status: s.status,
    escalatedAt: s.escalatedAt,
    updatedAt: s.updatedAt,
    managerRequested: s.managerRequested === true,
    claimedByUserId: s.claimedByUserId ?? null,
    claimedByName: s.claimedByName ?? null,
    claimedAt: s.claimedAt ?? null,
    eventId: s.eventId ?? eventId,
    messages: s.messages ?? [],
  };
}

export const supportEscalationsService = {
  async list(
    eventId: string,
    status: "open" | "done" | "all" = "open",
  ): Promise<SupportEscalationSummary[]> {
    if (!isApiEnabled()) return [];
    const data = await apiRequest<{ items: SupportEscalationSummary[] }>(
      `/events/${eventId}/support/escalations?status=${status}`,
      { audience: "admin" },
    );
    return data.items.map(mapSummary);
  },

  /** Open escalations for the header bell. */
  async listGlobal(
    status: "open" | "done" | "all" = "open",
  ): Promise<SupportEscalationSummary[]> {
    if (!isApiEnabled()) return [];
    const data = await apiRequest<{ items: SupportEscalationSummary[] }>(
      `/support/admin/escalations?status=${status}`,
      { audience: "admin" },
    );
    return data.items.map(mapSummary);
  },

  /** All Helper chats (AI + escalations), paginated. */
  async listChats(params: {
    scope?: "all" | "ai" | "escalated";
    status?: "open" | "done" | "all";
    audience?: "all" | "exhibitor" | "visitor";
    page?: number;
    pageSize?: number;
  } = {}): Promise<SupportChatListResult> {
    if (!isApiEnabled()) {
      return { items: [], total: 0, page: 1, pageSize: 20, totalPages: 1 };
    }
    const scope = params.scope ?? "all";
    const status = params.status ?? "open";
    const audience = params.audience ?? "all";
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? 20;
    const q = new URLSearchParams({
      scope,
      status,
      audience,
      page: String(page),
      pageSize: String(pageSize),
    });
    const data = await apiRequest<SupportChatListResult>(
      `/support/admin/chats?${q}`,
      { audience: "admin" },
    );
    return {
      ...data,
      items: (data.items ?? []).map(mapSummary),
    };
  },

  async get(eventId: string, sessionId: string): Promise<SupportEscalationDetail | null> {
    const data = await apiRequest<{ session: ApiSession }>(
      `/events/${eventId}/support/escalations/${sessionId}`,
      { audience: "admin" },
    );
    return mapSession(data.session, eventId);
  },

  async setStatus(
    eventId: string,
    sessionId: string,
    status: "open" | "done",
  ): Promise<void> {
    await apiRequest(`/events/${eventId}/support/escalations/${sessionId}`, {
      method: "PATCH",
      body: { status },
      audience: "admin",
    });
  },

  async claim(
    eventId: string,
    sessionId: string,
    force = false,
  ): Promise<SupportEscalationDetail> {
    const data = await apiRequest<{ session: ApiSession }>(
      `/events/${eventId}/support/escalations/${sessionId}/claim`,
      {
        method: "POST",
        body: { force },
        audience: "admin",
      },
    );
    return mapSession(data.session, eventId);
  },

  async release(
    eventId: string,
    sessionId: string,
    force = false,
  ): Promise<SupportEscalationDetail> {
    const data = await apiRequest<{ session: ApiSession }>(
      `/events/${eventId}/support/escalations/${sessionId}/release`,
      {
        method: "POST",
        body: { force },
        audience: "admin",
      },
    );
    return mapSession(data.session, eventId);
  },

  async reply(
    eventId: string,
    sessionId: string,
    content: string,
  ): Promise<SupportEscalationDetail | null> {
    const data = await apiRequest<{ session: ApiSession }>(
      `/events/${eventId}/support/escalations/${sessionId}/messages`,
      {
        method: "POST",
        body: { content },
        audience: "admin",
      },
    );
    return mapSession(data.session, eventId);
  },
};
